/**
 * Puts a buyer back where they were after the sign-in detour.
 *
 * `AuthScreen` cannot navigate — it has no navigation prop, and the app leaves
 * it simply because the navigator re-renders once `user` exists. So a person
 * who clicked "Subscribe to Elite", got sent to sign in, and signed up
 * successfully lands on the dashboard with their decision thrown away.
 *
 * This component watches for exactly that moment — a real, non-demo user
 * appearing while a fresh checkout intent is stored — and routes them back to
 * Pricing, where `PricingScreen` consumes the intent, preselects the plan and
 * billing period, and offers one button to finish.
 *
 * It renders nothing. It is mounted inside `NavigationContainer` as a sibling
 * of the navigator, the same placement and for the same reason as
 * `NotificationBanner`: that is what lets it navigate.
 *
 * 🔴 It deliberately does NOT open Stripe itself. Throwing someone at a card
 * form the instant they finish signing up reads as a trap, and on web
 * `openUrl` is `window.location.href` — they would lose the app entirely. One
 * visible tap on a named price is seamless; a surprise redirect is merely fast.
 *
 * 🔴 It checks `user.isDemo`, not just `user`. The shared demo session is a
 * real user object, and `/api/billing/checkout` is `requireAuth` +
 * `requireNotDemo` — resuming into it would land back on the same 403 the
 * guards at both call sites exist to avoid.
 */
import { useEffect, useRef } from "react";
import { useNavigation } from "@react-navigation/native";
import { useAuth } from "@/hooks/useAuth";
import { readPendingCheckoutIntent } from "@/lib/checkoutIntent";

export default function CheckoutResume(): null {
  const navigation = useNavigation<any>();
  const { user } = useAuth();
  // One redirect per app session. Without this, any later re-render while the
  // intent is still stored would keep yanking the reader back to Pricing.
  const sent = useRef(false);

  useEffect(() => {
    if (sent.current) return;
    if (!user || user.isDemo) return;
    if (!readPendingCheckoutIntent()) return;

    sent.current = true;
    try {
      navigation.navigate("Pricing");
    } catch {
      // A navigator that is not ready yet must never break sign-in. The intent
      // stays stored, and PricingScreen still consumes it if they arrive by
      // any other route within the TTL.
      sent.current = false;
    }
  }, [user, navigation]);

  return null;
}
